/**
 * PartyLink - Generic link for parties (persons or organizations)
 *
 * Automatically delegates to PersonLink or OrganizationLink based on party type.
 * This is useful when you have a party_id but need to handle both types uniformly.
 */

import type { PartyType } from '@/services/api/generated/partiesService';

import type { EntityLinkTriggerProps } from '../core/types';
import { OrganizationLink } from './OrganizationLink';
import { PersonLink } from './PersonLink';

export interface PartyLinkProps extends EntityLinkTriggerProps {
  /** ID of the party (person or organization) */
  entityId: string;
  /** Type of party - determines which link component to use */
  partyType: PartyType | 'person' | 'organization';
  /** Matter ID for scoped queries */
  matterId: string;
}

/**
 * Generic party link that delegates to PersonLink or OrganizationLink
 *
 * Use this when you have a party_id and party_type but want a single
 * component that handles both cases.
 *
 * @example
 * ```tsx
 * <PartyLink
 *   entityId={partyId}
 *   partyType={party.party_type}
 *   matterId={matterId}
 * >
 *   {partyName}
 * </PartyLink>
 * ```
 */
export const PartyLink = ({
  entityId,
  partyType,
  matterId,
  children,
  ...props
}: PartyLinkProps) => {
  // Format entityId as "matterId:entityId" for the underlying link components
  const formattedEntityId = `${matterId}:${entityId}`;

  if (partyType === 'person') {
    return (
      <PersonLink entityId={formattedEntityId} {...props}>
        {children}
      </PersonLink>
    );
  }

  if (partyType === 'organization') {
    return (
      <OrganizationLink entityId={formattedEntityId} {...props}>
        {children}
      </OrganizationLink>
    );
  }

  // Fallback for unknown party types
  return <span className="text-gray-900">{children}</span>;
};

PartyLink.displayName = 'PartyLink';
